import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  IconButton,
  Divider,
  Popover,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FlightIcon from '@mui/icons-material/Flight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { Tooltip } from '@mui/material';
import { pushToGoogleSheet } from '../utils/googleSheets';

// Reusable Location Field Component
const LocationAutocompleteField = ({ value, onChange, placeholder, error, helperText }) => {
  return (
    <TextField
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      variant="outlined"
      size="small"
      fullWidth
      error={!!error}
      helperText={helperText}
      sx={{
        width: '100%',
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          fontSize: '0.72rem',
          fontFamily: "'Poppins', sans-serif",
          backgroundColor: '#FAFAFA',
          pr: 1,
          '& fieldset': { borderColor: error ? '#d32f2f' : '#E8E0F0' },
          '&:hover fieldset': { borderColor: error ? '#d32f2f' : '#5B2D8E' },
          '&.Mui-focused fieldset': { borderColor: error ? '#d32f2f' : '#5B2D8E' }
        },
        '& .MuiInputBase-input': { py: 0.8, pl: 1.2 },
        '& .MuiFormHelperText-root': { fontSize: '0.6rem', ml: 0.5, mt: 0.2 }
      }}
    />
  );
};

const BookingForm = ({ tabValue, setTabValue, tripType, setTripType, initialPickup = '', initialDrop = '' }) => {
  const [airportTripType, setAirportTripType] = useState('drop');
  const [duration, setDuration] = useState('8 Hours / 80 Kms');

  // Input states
  const [pickupLocation, setPickupLocation] = useState(initialPickup);
  const [dropLocation, setDropLocation] = useState(initialDrop);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async () => {
    const newErrors = {};

    // 1. Validate Required Fields (Common)
    if (!pickupLocation.trim()) {
      newErrors.pickup = 'Pickup Location is required';
    }
    if (!dropLocation.trim()) {
      newErrors.drop = 'Drop Location is required';
    }
    if (!selectedDate) {
      newErrors.date = 'Pickup Date is required';
    }

    // 2. Validate Phone Number (Indian 10-digit format)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(phoneNumber)) {
      newErrors.phone = 'Enter a valid 10-digit number (starting with 6-9)';
    }

    // 3. Tab-Specific Validations
    if (tabValue === 0 && tripType === 'roundtrip') {
      if (!selectedDropDate) {
        newErrors.dropDate = 'Return Date is required';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    try {
      let sheetName = '';
      let values = [];
      const commonTimeStr = `${timeState.h}:${timeState.m} ${timeState.p}`;
      const dateStr = selectedDate ? selectedDate.toLocaleDateString('en-GB') : '';
      const dropDateStr = selectedDropDate ? selectedDropDate.toLocaleDateString('en-GB') : '';
      const dropTimeStr = dropTimeState.h ? `${dropTimeState.h}:${dropTimeState.m} ${dropTimeState.p}` : '';
      const destStr = destinations.map(d => d.value).join(', ');

      if (tabValue === 0) { // Outstation
        sheetName = 'Outstation';
        values = [tripType, dateStr, commonTimeStr, dropDateStr, dropTimeStr, pickupLocation, destStr, dropLocation, phoneNumber];
      } else if (tabValue === 1) { // Airport Transfer
        sheetName = 'Airport Transfer';
        values = [airportTripType, dateStr, commonTimeStr, dropDateStr, dropTimeStr, pickupLocation, destStr, dropLocation, phoneNumber];
      } else if (tabValue === 2) { // Hourly Rental
        sheetName = 'Hourly Rental';
        values = [dateStr, commonTimeStr, duration, pickupLocation, destStr, dropLocation, phoneNumber];
      }

      const safeValues = values.map(v => v || '');
      await pushToGoogleSheet(sheetName, safeValues);

      setShowSuccess(true);

      // Reset form
      setPickupLocation('');
      setDropLocation('');
      setPhoneNumber('');
      setDestinations([]);
    } catch (err) {
      console.error(err);
      alert('Failed to submit booking: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Destinations logic
  const [destinations, setDestinations] = useState([]);
  const addDestination = () => {
    setDestinations(prev => [...prev, { id: Date.now(), value: '' }]);
  };
  const removeDestination = (id) => {
    setDestinations(prev => prev.filter((d) => d.id !== id));
  };
  const updateDestination = (id, val) => {
    setDestinations(prev => prev.map((d) => (d.id === id ? { ...d, value: val } : d)));
  };

  // Date Popover State & Logic
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);

  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [activeDateTarget, setActiveDateTarget] = useState('pickup');
  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [selectedDropDate, setSelectedDropDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(todayDate);

  const prevMonth = () => {
    const newMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1);
    setCalendarMonth(newMonth);
  };
  const nextMonth = () => {
    const newMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1);
    setCalendarMonth(newMonth);
  };

  const getFormatDate = (date) => {
    if (!date) return 'Select Date';
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const diff = d.getTime() - todayDate.getTime();
    const daysDiff = Math.round(diff / 86400000);

    if (daysDiff === 0) return 'Today';
    if (daysDiff === 1) return 'Tomorrow';

    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Time Popover State & Logic
  const [timeAnchorEl, setTimeAnchorEl] = useState(null);
  const [activeTimeTarget, setActiveTimeTarget] = useState('pickup');
  const [timeState, setTimeState] = useState({ h: '02', m: '30', p: 'PM' });
  const [dropTimeState, setDropTimeState] = useState({ h: '', m: '', p: '' });
  const [tempTime, setTempTime] = useState({ h: '02', m: '30', p: 'PM' });

  const handleOpenTime = (e, target) => {
    setActiveTimeTarget(target);
    setTempTime(target === 'pickup' ? timeState : (dropTimeState.h ? dropTimeState : { h: '02', m: '30', p: 'PM' }));
    setTimeAnchorEl(e.currentTarget);
  };

  const handleConfirmTime = () => {
    if (activeTimeTarget === 'pickup') {
      setTimeState(tempTime);
    } else {
      setDropTimeState(tempTime);
    }
    setTimeAnchorEl(null);
  };

  // Boundary helper logic
  const getBoundaryString = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30);
    const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const min = now.getMinutes();
    const minSuffix = min < 10 ? `0${min}` : min;
    const hour24 = now.getHours();
    const hour12 = hour24 % 12 || 12;
    const p = hour24 >= 12 ? 'PM' : 'AM';
    const hourStr = hour12 < 10 ? `0${hour12}` : hour12;
    return `Select after ${dateStr} ${hourStr}:${minSuffix} ${p}`;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 5,
        width: '100%',
        height: 'auto',
      }}
    >
      <Box
        sx={{
          backgroundColor: { xs: '#ffffff', md: 'rgba(255, 255, 255, 0.97)' },
          borderRadius: { xs: 0, md: '24px' },
          boxShadow: { xs: 'none', md: '0 25px 60px rgba(0,0,0,0.15), 0 8px 18px rgba(91,45,142,0.05)' },
          backdropFilter: { xs: 'none', md: 'blur(30px)' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'calc(100dvh - 80px)', md: 'min(80vh, 600px)' },
          mt: { xs: '80px', md: 0 },
          border: { xs: 'none', md: '1px solid rgba(255,255,255,0.45)' },
          width: { xs: '100vw', sm: 380, md: 360 },
          mx: { xs: 0, md: 0 },
        }}
      >
        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 56,
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: { xs: '#ffffff', md: 'transparent' },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.62rem', md: '0.5rem' },
              minHeight: 56,
              py: 0.5,
              color: '#666',
              fontFamily: "'Poppins', sans-serif",
              transition: 'all 0.3s ease',
              '&.Mui-selected': {
                color: '#5B2D8E',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#5B2D8E',
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab
            icon={<ApartmentIcon sx={{ fontSize: 18 }} />}
            label="Outstation"
            iconPosition="top"
            sx={{ gap: 0.2 }}
          />
          <Tab
            icon={<FlightIcon sx={{ fontSize: 18 }} />}
            label="Airport Transfer"
            iconPosition="top"
            sx={{ gap: 0.2 }}
          />
          <Tab
            icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
            label="Hourly Rental"
            iconPosition="top"
            sx={{ gap: 0.2 }}
          />
        </Tabs>

        {/* Form Content */}
        <Box
          sx={{
            px: { xs: 2.5, md: 2 },
            py: { xs: 2.5, md: 1.5 },
            overflowY: 'auto',
            flex: 1,
            '&::-webkit-scrollbar': { width: '5px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb': { background: '#dcdcdc', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb:hover': { background: '#5B2D8E' }
          }}
        >
          <Box>
            {tabValue === 0 && (
              <Box>
                {/* Trip Type Toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    backgroundColor: '#F5F5F8',
                    borderRadius: '28px',
                    p: '3px',
                    mb: 1.5,
                  }}
                >
                  <Button
                    onClick={() => setTripType('oneway')}
                    sx={{
                      flex: 1,
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      py: 0.8,
                      backgroundColor: tripType === 'oneway' ? '#5B2D8E' : 'transparent',
                      color: tripType === 'oneway' ? '#fff' : '#888',
                      boxShadow: tripType === 'oneway' ? '0 4px 12px rgba(91,45,142,0.3)' : 'none',
                      fontFamily: "'Poppins', sans-serif",
                      '&:hover': {
                        backgroundColor: tripType === 'oneway' ? '#4A2474' : 'rgba(255,255,255,0.5)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    One Way
                  </Button>
                  <Button
                    onClick={() => setTripType('roundtrip')}
                    sx={{
                      flex: 1,
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      py: 0.8,
                      backgroundColor: tripType === 'roundtrip' ? '#5B2D8E' : 'transparent',
                      color: tripType === 'roundtrip' ? '#fff' : '#888',
                      boxShadow: tripType === 'roundtrip' ? '0 4px 12px rgba(91,45,142,0.3)' : 'none',
                      fontFamily: "'Poppins', sans-serif",
                      '&:hover': {
                        backgroundColor: tripType === 'roundtrip' ? '#4A2474' : 'rgba(255,255,255,0.5)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Round Trip
                  </Button>
                </Box>
              </Box>
            )}

            {tabValue === 1 && (
              <Box sx={{ display: 'flex', backgroundColor: '#F5F5F8', borderRadius: '28px', p: '3px', mb: 1.5 }}>
                <Button
                  onClick={() => setAirportTripType('drop')}
                  sx={{
                    flex: 1, borderRadius: '25px', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', py: 0.8,
                    backgroundColor: airportTripType === 'drop' ? '#5B2D8E' : 'transparent',
                    color: airportTripType === 'drop' ? '#fff' : '#888',
                    boxShadow: airportTripType === 'drop' ? '0 4px 12px rgba(91,45,142,0.3)' : 'none',
                    fontFamily: "'Poppins', sans-serif",
                    '&:hover': { backgroundColor: airportTripType === 'drop' ? '#4A2474' : 'rgba(255,255,255,0.5)' },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Airport Drop
                </Button>
                <Button
                  onClick={() => setAirportTripType('pickup')}
                  sx={{
                    flex: 1, borderRadius: '25px', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', py: 0.8,
                    backgroundColor: airportTripType === 'pickup' ? '#5B2D8E' : 'transparent',
                    color: airportTripType === 'pickup' ? '#fff' : '#888',
                    boxShadow: airportTripType === 'pickup' ? '0 4px 12px rgba(91,45,142,0.3)' : 'none',
                    fontFamily: "'Poppins', sans-serif",
                    '&:hover': { backgroundColor: airportTripType === 'pickup' ? '#4A2474' : 'rgba(255,255,255,0.5)' },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Airport Pickup
                </Button>
              </Box>
            )}

            {/* Pickup / Drop Time Fields based on Trip Type */}
            <Box sx={{ mb: 1.5 }}>
              {(tabValue === 0 && tripType === 'oneway') || tabValue === 2 ? (
                <>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#333', mb: 0.4, fontFamily: "'Poppins', sans-serif" }}>
                    Pickup Time *
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.2 }}>
                    <Box onClick={(e) => { setActiveDateTarget('pickup'); setDateAnchorEl(e.currentTarget); }} sx={{ flex: 1, border: errors.date ? '1.5px solid #d32f2f' : '1.5px solid #E8E0F0', borderRadius: '8px', p: '8px 12px', display: 'flex', alignItems: 'center', gap: 1.2, cursor: 'pointer', '&:hover': { borderColor: errors.date ? '#d32f2f' : '#5B2D8E' }, transition: 'border-color 0.2s' }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, color: '#5B2D8E' }} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#333', fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                        {getFormatDate(selectedDate)}
                      </Typography>
                    </Box>
                    <Box onClick={(e) => handleOpenTime(e, 'pickup')} sx={{ flex: 1, border: '1.5px solid #E8E0F0', borderRadius: '8px', p: '8px 12px', display: 'flex', alignItems: 'center', gap: 1.2, cursor: 'pointer', '&:hover': { borderColor: '#5B2D8E' }, transition: 'border-color 0.2s' }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: '#5B2D8E' }} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#333', fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                        {`${timeState.h}:${timeState.m} ${timeState.p}`}
                      </Typography>
                    </Box>
                  </Box>
                  {errors.date && <Typography sx={{ fontSize: '0.62rem', color: '#d32f2f', mt: 0.5, ml: 0.5 }}>{errors.date}</Typography>}
                </>
              ) : (
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {/* Round Trip Left Column */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#333', fontFamily: "'Poppins', sans-serif" }}>Pickup Time *</Typography>
                    <Box onClick={(e) => { setActiveDateTarget('pickup'); setDateAnchorEl(e.currentTarget); }} sx={{ border: errors.date ? '1.5px solid #d32f2f' : '1.5px solid #E8E0F0', borderRadius: '8px', p: '8px 12px', display: 'flex', alignItems: 'center', gap: 1.2, cursor: 'pointer', '&:hover': { borderColor: errors.date ? '#d32f2f' : '#5B2D8E' }, transition: 'border-color 0.2s' }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, color: '#5B2D8E' }} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#333', fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                        {getFormatDate(selectedDate)}
                      </Typography>
                    </Box>
                    <Box onClick={(e) => handleOpenTime(e, 'pickup')} sx={{ border: '1.5px solid #E8E0F0', borderRadius: '8px', p: '8px 12px', display: 'flex', alignItems: 'center', gap: 1.2, cursor: 'pointer', '&:hover': { borderColor: '#5B2D8E' }, transition: 'border-color 0.2s' }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: '#5B2D8E' }} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#333', fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                        {`${timeState.h}:${timeState.m} ${timeState.p}`}
                      </Typography>
                    </Box>
                    {errors.date && <Typography sx={{ fontSize: '0.62rem', color: '#d32f2f', mt: 0.1, ml: 0.5 }}>{errors.date}</Typography>}
                  </Box>

                  {/* Round Trip Right Column */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#333', fontFamily: "'Poppins', sans-serif" }}>Drop Time *</Typography>
                    <Box onClick={(e) => { setActiveDateTarget('drop'); setDateAnchorEl(e.currentTarget); }} sx={{ border: errors.dropDate ? '1.5px solid #d32f2f' : '1.5px solid #E8E0F0', borderRadius: '8px', p: '8px 12px', display: 'flex', alignItems: 'center', gap: 1.2, cursor: 'pointer', '&:hover': { borderColor: errors.dropDate ? '#d32f2f' : '#5B2D8E' }, transition: 'border-color 0.2s' }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, color: '#5B2D8E' }} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#333', fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                        {getFormatDate(selectedDropDate)}
                      </Typography>
                    </Box>
                    <Box onClick={(e) => handleOpenTime(e, 'drop')} sx={{ border: '1.5px solid #E8E0F0', borderRadius: '8px', p: '8px 12px', display: 'flex', alignItems: 'center', gap: 1.2, cursor: 'pointer', '&:hover': { borderColor: '#5B2D8E' }, transition: 'border-color 0.2s' }}>
                      <AccessTimeIcon sx={{ fontSize: 16, color: '#5B2D8E' }} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#333', fontWeight: 500, fontFamily: "'Poppins', sans-serif" }}>
                        {dropTimeState.h ? `${dropTimeState.h}:${dropTimeState.m} ${dropTimeState.p}` : 'Select Time'}
                      </Typography>
                    </Box>
                    {errors.dropDate && <Typography sx={{ fontSize: '0.62rem', color: '#d32f2f', mt: 0.1, ml: 0.5 }}>{errors.dropDate}</Typography>}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Hourly Rental Specific: Duration Select */}
            {tabValue === 2 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#333', mb: 0.4, fontFamily: "'Poppins', sans-serif" }}>
                  Duration / Distance *
                </Typography>
                <Select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  size="small"
                  fullWidth
                  displayEmpty
                  sx={{
                    borderRadius: '8px',
                    fontSize: '0.78rem',
                    fontFamily: "'Poppins', sans-serif",
                    backgroundColor: '#FAFAFA',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E8E0F0' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#5B2D8E' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5B2D8E' },
                  }}
                >
                  <MenuItem value="8 Hours / 80 Kms" sx={{ fontSize: '0.78rem', fontFamily: "'Poppins', sans-serif" }}>8 Hours / 80 Kms</MenuItem>
                  <MenuItem value="12 Hours / 120 Kms" sx={{ fontSize: '0.78rem', fontFamily: "'Poppins', sans-serif" }}>12 Hours / 120 Kms</MenuItem>
                </Select>
              </Box>
            )}

            {/* Travel Plan */}
            <Box sx={{ mb: 1.2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.8,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    color: '#333',
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  Travel Plan *
                </Typography>
                <Button
                  onClick={addDestination}
                  startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                  sx={{
                    textTransform: 'none',
                    color: '#5B2D8E',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    p: 0,
                    minWidth: 'auto',
                    fontFamily: "'Poppins', sans-serif",
                    '&:hover': { backgroundColor: 'transparent', color: '#4A2474' },
                  }}
                >
                  Add Destination
                </Button>
              </Box>

              {/* Dotted Timeline Wrapper */}
              <Box sx={{ position: 'relative' }}>
                <Box sx={{ position: 'absolute', left: 18, top: 18, bottom: 20, zIndex: 0, width: 0, borderLeft: '1.5px dashed #A898D2' }} />

                {/* Pickup */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8, position: 'relative', zIndex: 1 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#333', minWidth: 38, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)', py: 0.5, fontFamily: "'Poppins', sans-serif" }}>
                    Pickup
                  </Typography>
                  <LocationAutocompleteField
                    value={pickupLocation}
                    onChange={(val) => setPickupLocation(val)}
                    placeholder="Enter City, Airport, Address or Hotel"
                    error={errors.pickup}
                    helperText={errors.pickup}
                  />
                </Box>

                {/* Dynamic Destinations */}
                {destinations.map((dest, index) => (
                  <Box key={dest.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8, position: 'relative', zIndex: 1 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#333', minWidth: 38, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)', py: 0.5, fontFamily: "'Poppins', sans-serif" }}>
                      Dest {index + 1}
                    </Typography>
                    <LocationAutocompleteField
                      value={dest.value}
                      onChange={(val) => updateDestination(dest.id, val)}
                      placeholder="Enter City, Airport, Address or Hotel"
                      showMyLocation={false}
                    />
                    <IconButton size="small" onClick={() => removeDestination(dest.id)} sx={{ color: '#999', flexShrink: 0, p: 0.5, bgcolor: '#fff', border: '1px solid #E8E0F0', borderRadius: '8px', '&:hover': { color: '#ff4d4f', borderColor: '#ff4d4f', bgcolor: '#fff5f5' } }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}

                {/* Drop */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#333', minWidth: 38, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.9)', py: 0.5, fontFamily: "'Poppins', sans-serif" }}>
                    Drop
                  </Typography>
                  <LocationAutocompleteField
                    value={dropLocation}
                    onChange={(val) => setDropLocation(val)}
                    placeholder="Enter City, Airport, Address or Hotel"
                    error={errors.drop}
                    helperText={errors.drop}
                    showMyLocation={false}
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 1, borderColor: '#F0EDF5' }} />

            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#333', mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>
                Phone Number *
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.8 }}>
                <Box sx={{ border: '1.5px solid #E8E0F0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1.2, minWidth: 48, backgroundColor: '#FAFAFA' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#333', fontFamily: "'Poppins', sans-serif" }}>+91</Typography>
                </Box>
                <TextField
                  value={phoneNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, ''); // Remove non-digits
                    if (val.length <= 10) setPhoneNumber(val); // Limit to 10 digits
                  }}
                  placeholder="Enter 10-digit number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '0.72rem',
                      fontFamily: "'Poppins', sans-serif",
                      backgroundColor: '#FAFAFA',
                      '& fieldset': { borderColor: errors.phone ? '#d32f2f' : '#E8E0F0' },
                      '&:hover fieldset': { borderColor: errors.phone ? '#d32f2f' : '#5B2D8E' },
                      '&.Mui-focused fieldset': { borderColor: errors.phone ? '#d32f2f' : '#5B2D8E' }
                    },
                    '& .MuiInputBase-input': { py: 0.8, pl: 1.2 },
                    '& .MuiFormHelperText-root': { fontSize: '0.6rem', ml: 0.5, mt: 0.2 }
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Quote Graphic Banner */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, backgroundColor: '#F3F0F7', borderRadius: '12px', p: '14px 18px', mb: 2, alignItems: 'flex-start', gap: 1 }}>
            <Typography sx={{ fontSize: '3rem', color: '#BCAAD4', lineHeight: 0.5, fontFamily: 'serif', mt: 1 }}>“</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#333', mt: 0.5, fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.2px' }}>
              Find the right vehicle for your journey, effortlessly!
            </Typography>
          </Box>
        </Box>

        {/* Static Footer */}
        <Box sx={{ px: 2, pt: 0.5, pb: 2 }}>
          {true ? (
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.85rem',
                py: 1.5,
                background: 'linear-gradient(135deg, #5B2D8E 0%, #7B4BB3 100%)',
                boxShadow: '0 6px 20px rgba(91, 45, 142, 0.35)',
                fontFamily: "'Poppins', sans-serif",
                position: 'relative',
                overflow: 'hidden',
                '&:hover': { background: 'linear-gradient(135deg, #4A2474 0%, #6A3CA0 100%)', boxShadow: '0 8px 28px rgba(91, 45, 142, 0.45)', transform: 'translateY(-1px)' },
                transition: 'all 0.3s ease',
              }}
            >
              <Box
                component="img"
                src="/vehicles-banner.png"
                sx={{
                  position: 'absolute',
                  left: -15,
                  bottom: -27,
                  height: 100,
                  width: 'auto',
                  objectFit: 'contain',
                  opacity: 0.8,
                  filter: 'invert(1) grayscale(1) brightness(1.5)',
                  mixBlendMode: 'screen',
                  zIndex: 1,
                  pointerEvents: 'none'
                }}
              />
              <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center' }}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
                {!isSubmitting && <ArrowForwardIcon sx={{ fontSize: 18, ml: 0.5 }} />}
              </Box>
            </Button>
          ) : (
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.85rem',
                py: 1.5,
                background: '#5B2D8E',
                boxShadow: '0 6px 20px rgba(91, 45, 142, 0.35)',
                fontFamily: "'Poppins', sans-serif",
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 0.8,
                overflow: 'hidden',
                '&:hover': { background: '#4A2474', boxShadow: '0 8px 28px rgba(91, 45, 142, 0.45)', transform: 'translateY(-1px)' },
                transition: 'all 0.3s ease',
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Check Fares'}
              {!isSubmitting && <ArrowForwardIcon sx={{ fontSize: 18 }} />}
            </Button>
          )}
        </Box>
      </Box>

      {/* Date Picker Popover */}
      <Popover
        open={Boolean(dateAnchorEl)}
        anchorEl={dateAnchorEl}
        onClose={() => setDateAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 3, borderRadius: '16px', width: { xs: 280, md: 300 }, mt: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.12)', overflow: 'hidden' }
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: '#333' }}>Select Date</Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              const d = new Date(todayDate.getTime());
              if (activeDateTarget === 'pickup') {
                setSelectedDate(d);
              } else {
                setSelectedDropDate(d);
              }
              setCalendarMonth(d);
              // setDateAnchorEl(null);
            }}
            sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 600, color: '#5B2D8E', p: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
          >
            Today
          </Button>
        </Box>
        {/* Rest of Date Picker content... */}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 0.5 }}>
          <IconButton
            size="small"
            onClick={prevMonth}
            disabled={calendarMonth.getFullYear() === todayDate.getFullYear() && calendarMonth.getMonth() === todayDate.getMonth()}
            sx={{ color: '#5B2D8E', bgcolor: '#f5f0fa', '&:hover': { bgcolor: '#ece4f5' }, '&:disabled': { opacity: 0.3 } }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: '#333' }}>
            {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Typography>
          <IconButton
            size="small"
            onClick={nextMonth}
            sx={{ color: '#5B2D8E', bgcolor: '#f5f0fa', '&:hover': { bgcolor: '#ece4f5' } }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, textAlign: 'center', mb: 1 }}>
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(day => (
            <Typography key={day} sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#666' }}>{day}</Typography>
          ))}

          {/* Empty offset days */}
          {[...Array(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1).getDay() === 0 ? 6 : new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1).getDay() - 1)].map((_, i) => (
            <Box key={`empty-${i}`} />
          ))}

          {/* Actual days */}
          {[...Array(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate())].map((_, i) => {
            const thisDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), i + 1);
            thisDay.setHours(0, 0, 0, 0);
            const isPast = thisDay.getTime() < todayDate.getTime();
            const isSelected = activeDateTarget === 'pickup'
              ? (selectedDate && selectedDate.getTime() === thisDay.getTime())
              : (selectedDropDate && selectedDropDate.getTime() === thisDay.getTime());

            return (
              <Box
                key={i}
                sx={{
                  fontSize: '0.75rem',
                  p: 0.8,
                  borderRadius: '6px',
                  cursor: isPast ? 'not-allowed' : 'pointer',
                  opacity: isPast ? 0.3 : 1,
                  bgcolor: isSelected ? '#5B2D8E' : 'transparent',
                  color: isSelected ? '#fff' : '#333',
                  fontWeight: isSelected ? 600 : 400,
                  '&:hover': { bgcolor: isPast ? 'transparent' : (isSelected ? '#5B2D8E' : '#f0f0f0') }
                }}
                onClick={() => {
                  if (!isPast) {
                    if (activeDateTarget === 'pickup') {
                      setSelectedDate(new Date(thisDay.getTime()));
                    } else {
                      setSelectedDropDate(new Date(thisDay.getTime()));
                    }
                    setDateAnchorEl(null);
                  }
                }}
              >
                {i + 1}
              </Box>
            );
          })}
        </Box>
      </Popover>

      {/* Time Picker Popover */}
      <Popover
        open={Boolean(timeAnchorEl)}
        anchorEl={timeAnchorEl}
        onClose={() => setTimeAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 3, borderRadius: '16px', width: 240, mt: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.12)', overflow: 'hidden' }
          }
        }}
      >
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 2.5, fontFamily: "'Poppins', sans-serif", color: '#333' }}>Select Time</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 0.5, width: 45, textAlign: 'center' }}>
            <input
              type="number"
              value={tempTime.h}
              onChange={(e) => {
                let val = e.target.value;
                if (val.length > 2) val = val.slice(1, 3);
                setTempTime({ ...tempTime, h: val });
              }}
              onBlur={() => {
                let v = parseInt(tempTime.h);
                if (isNaN(v) || v < 1) v = 1;
                if (v > 12) v = 12;
                setTempTime({ ...tempTime, h: v.toString().padStart(2, '0') });
              }}
              style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 500, fontFamily: "'Poppins', sans-serif", padding: 0 }}
            />
          </Box>
          <Typography sx={{ fontWeight: 600 }}>:</Typography>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 0.5, width: 45, textAlign: 'center' }}>
            <input
              type="number"
              value={tempTime.m}
              onChange={(e) => {
                let val = e.target.value;
                if (val.length > 2) val = val.slice(1, 3);
                setTempTime({ ...tempTime, m: val });
              }}
              onBlur={() => {
                let v = parseInt(tempTime.m);
                if (isNaN(v) || v < 0) v = 0;
                if (v > 59) v = 59;
                setTempTime({ ...tempTime, m: v.toString().padStart(2, '0') });
              }}
              style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'center', fontSize: '0.9rem', fontWeight: 500, fontFamily: "'Poppins', sans-serif", padding: 0 }}
            />
          </Box>
          <Box
            onClick={() => setTempTime({ ...tempTime, p: tempTime.p === 'AM' ? 'PM' : 'AM' })}
            sx={{ bgcolor: '#5B2D8E', color: '#fff', borderRadius: '8px', p: 1, width: 45, textAlign: 'center', cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: '#4A2474' } }}
          >
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{tempTime.p}</Typography>
          </Box>
        </Box>
        <Button
          fullWidth
          variant="contained"
          sx={{ bgcolor: '#5B2D8E', textTransform: 'none', borderRadius: '8px', py: 1, boxShadow: 'none', '&:hover': { bgcolor: '#4A2474', boxShadow: 'none' } }}
          onClick={handleConfirmTime}
        >
          Confirm
        </Button>
      </Popover>

      {/* Success Dialog */}
      <Dialog
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            padding: '20px',
            maxWidth: '340px',
            textAlign: 'center'
          }
        }}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#f0fdf4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              animation: 'scaleIn 0.5s ease-out forwards',
              '@keyframes scaleIn': {
                '0%': { transform: 'scale(0)', opacity: 0 },
                '100%': { transform: 'scale(1)', opacity: 1 }
              }
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 50, color: '#22c55e' }} />
          </Box>

          <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#1a1a1a', mb: 1, fontFamily: "'Poppins', sans-serif" }}>
            Booking Confirmed!
          </Typography>

          <Typography sx={{ color: '#666', fontSize: '0.9rem', lineHeight: 1.6, mb: 4, fontFamily: "'Poppins', sans-serif" }}>
            Thank you for choosing Vihaan Tours & Travels. Our team will reach out to you shortly to finalize your trip details.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            onClick={() => setShowSuccess(false)}
            sx={{
              borderRadius: '12px',
              py: 1.5,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              backgroundColor: '#5B2D8E',
              boxShadow: '0 4px 12px rgba(91,45,142,0.2)',
              '&:hover': { backgroundColor: '#4A2474', boxShadow: '0 6px 16px rgba(91,45,142,0.3)' }
            }}
          >
            Great, Thanks!
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BookingForm;
